"use client";

import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

export interface Tab {
    label: string;
    value: string;
    disabled?: boolean;
}

interface TabsProps {
    tabs: Tab[];
    activeTab: string;
    onTabChange: ( tab: string ) => void;
    className?: string;
    equalWidth?: boolean;
    fixedIndicatorWidth?: number;
    itemClassName?: string;
    activeItemClassName?: string;
    inactiveItemClassName?: string;
}

export function Tabs(
    {
        tabs,
        activeTab,
        onTabChange,
        className = "",
        equalWidth = false,
        fixedIndicatorWidth,
        itemClassName = "",
        activeItemClassName = "",
        inactiveItemClassName = "",
    }: TabsProps,
) {
    const tabRefs = useRef<(HTMLButtonElement | null)[]>( [] );
    const [ underlineStyle, setUnderlineStyle ] = useState<{ width: number; left: number }>( { width: 0, left: 0 } );

    useEffect( () => {
        const activeIndex = tabs.findIndex( ( tab ) => tab.value === activeTab );
        if ( activeIndex === -1 ) return;
        const activeElement = tabRefs.current[activeIndex];
        if ( !activeElement ) return;

        const width = fixedIndicatorWidth ?? activeElement.offsetWidth;

        setUnderlineStyle( {
            width,
            left: activeElement.offsetLeft + ( activeElement.offsetWidth - width ) / 2, // 居中
        } );
    }, [ activeTab, tabs, fixedIndicatorWidth ] );

    return (
        <div className={cn( "group relative flex w-full border-b border-stroke", !equalWidth && "gap-4", className )}>
            {tabs.map( ( tab, index ) => {
                const isActive = activeTab === tab.value;
                const isDisabled = Boolean( tab.disabled );
                return (
                    <button
                        key={tab.value}
                        ref={( el ) => {
                            tabRefs.current[index] = el;
                        }}
                        onClick={() => {
                            if ( isDisabled ) return;
                            onTabChange( tab.value );
                        }}
                        disabled={isDisabled}
                        data-slot="tab-item"
                        data-active={isActive}
                        data-disabled={isDisabled}
                        className={cn(
                            "h-11 flex items-center justify-center font-medium text-sm transition-colors",
                            equalWidth && "flex-1",
                            itemClassName,
                            isActive ? cn( "text-t1", activeItemClassName ) : cn( "text-t3", inactiveItemClassName ),
                            isDisabled && "opacity-40 cursor-not-allowed",
                        )}
                    >
                        {tab.label}
                    </button>
                );
            } )}

            <div
                className="absolute bottom-0 h-0.5 bg-t1 transition-all duration-300 ease-in-out"
                data-slot="indicator"
                style={{ width: `${underlineStyle.width}px`, left: `${underlineStyle.left}px` }}
            />
        </div>
    );
}
